import { useEffect, useSyncExternalStore } from 'react';
import en from '@/i18n/chat/en.json';
import es from '@/i18n/chat/es.json';
import zhCN from '@/i18n/chat/zh-CN.json';
import vi from '@/i18n/chat/vi.json';
import ko from '@/i18n/chat/ko.json';
import ar from '@/i18n/chat/ar.json';
import hi from '@/i18n/chat/hi.json';
import tl from '@/i18n/chat/tl.json';
import fr from '@/i18n/chat/fr.json';
import ptBR from '@/i18n/chat/pt-BR.json';

/**
 * Chat / intake i18n engine.
 *
 * A SELF-CONTAINED localizer for the website chat widget and the document
 * intake — deliberately independent of the site-wide react-i18next instance so
 * the assistant's language never fights the rest of the page. Every locale
 * bundle falls back PER-KEY to English (`chat/<locale>.json` then `en.json`),
 * so a missing translation can never render an empty string.
 *
 * The visitor's choice persists in localStorage (wrapped in try/catch) and is
 * sent with every `site-chat` request as `locale`. Arabic sets dir="rtl" on the
 * widget only. Dates/numbers/file sizes go through `Intl`.
 *
 * SSR-safe: detection only runs on the client (hydrateChatLocale), so the
 * pre-rendered HTML always renders the English default and never mismatches.
 */

export type ChatLocale =
  | 'en' | 'es' | 'zh-CN' | 'vi' | 'ko' | 'ar' | 'hi' | 'tl' | 'fr' | 'pt-BR';

export interface ChatLocaleMeta {
  code: ChatLocale;
  /** Native display name (e.g. "Español", "中文"). */
  native: string;
  /** English label used in admin + the system prompt. */
  english: string;
  dir: 'ltr' | 'rtl';
}

/** Supported locales (Texas/US most-common order after English). */
export const CHAT_LOCALES: ChatLocaleMeta[] = [
  { code: 'en', native: 'English', english: 'English', dir: 'ltr' },
  { code: 'es', native: 'Español', english: 'Spanish', dir: 'ltr' },
  { code: 'zh-CN', native: '中文', english: 'Chinese (Simplified)', dir: 'ltr' },
  { code: 'vi', native: 'Tiếng Việt', english: 'Vietnamese', dir: 'ltr' },
  { code: 'ko', native: '한국어', english: 'Korean', dir: 'ltr' },
  { code: 'ar', native: 'العربية', english: 'Arabic', dir: 'rtl' },
  { code: 'hi', native: 'हिन्दी', english: 'Hindi', dir: 'ltr' },
  { code: 'tl', native: 'Filipino', english: 'Tagalog', dir: 'ltr' },
  { code: 'fr', native: 'Français', english: 'French', dir: 'ltr' },
  { code: 'pt-BR', native: 'Português (BR)', english: 'Portuguese (Brazil)', dir: 'ltr' },
];

const LOCALE_BY_CODE: Record<ChatLocale, ChatLocaleMeta> = CHAT_LOCALES.reduce(
  (acc, l) => { acc[l.code] = l; return acc; },
  {} as Record<ChatLocale, ChatLocaleMeta>,
);

const BUNDLES: Record<ChatLocale, Record<string, string>> = {
  en: en as Record<string, string>,
  es: es as Record<string, string>,
  'zh-CN': zhCN as Record<string, string>,
  vi: vi as Record<string, string>,
  ko: ko as Record<string, string>,
  ar: ar as Record<string, string>,
  hi: hi as Record<string, string>,
  tl: tl as Record<string, string>,
  fr: fr as Record<string, string>,
  'pt-BR': ptBR as Record<string, string>,
};

const SUPPORTED = new Set<string>(CHAT_LOCALES.map((l) => l.code));
const STORAGE_KEY = 'cendien_chat_locale';

/** Maps a raw BCP-47 tag (e.g. "pt-PT", "zh-Hant", "fil") onto a supported code. */
export function normalizeLocaleTag(tag: string): ChatLocale | null {
  const raw = (tag || '').trim();
  if (!raw) return null;
  const lower = raw.toLowerCase();
  if (SUPPORTED.has(raw)) return raw as ChatLocale;
  if (lower.startsWith('zh')) {
    // Traditional / HK / TW still resolve to the Simplified bundle we ship.
    return 'zh-CN';
  }
  if (lower.startsWith('pt')) return 'pt-BR';
  if (lower.startsWith('fil') || lower.startsWith('tl')) return 'tl';
  const base = lower.split('-')[0];
  for (const l of CHAT_LOCALES) {
    if (l.code.toLowerCase().split('-')[0] === base) return l.code;
  }
  return null;
}

/** First supported locale among navigator.languages, else English. */
export function detectChatLocale(): ChatLocale {
  try {
    const langs = (typeof navigator !== 'undefined' && Array.isArray(navigator.languages) && navigator.languages.length)
      ? navigator.languages
      : (typeof navigator !== 'undefined' && navigator.language ? [navigator.language] : []);
    for (const tag of langs) {
      const hit = normalizeLocaleTag(String(tag));
      if (hit) return hit;
    }
  } catch { /* ignore */ }
  return 'en';
}

export function getStoredChatLocale(): ChatLocale | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v && SUPPORTED.has(v)) return v as ChatLocale;
  } catch { /* localStorage may be blocked */ }
  return null;
}

function persistChatLocale(locale: ChatLocale): void {
  try { localStorage.setItem(STORAGE_KEY, locale); } catch { /* ignore */ }
}

// ── Tiny external store (so every consumer re-renders on a language change) ──
let current: ChatLocale = 'en';
let hydrated = false;
const listeners = new Set<() => void>();

function emit(): void { listeners.forEach((l) => l()); }
function subscribe(cb: () => void): () => void { listeners.add(cb); return () => { listeners.delete(cb); }; }
function getSnapshot(): ChatLocale { return current; }

/** Applies the stored/detected locale once on the client (never during SSR). */
export function hydrateChatLocale(): void {
  if (hydrated || typeof window === 'undefined') return;
  hydrated = true;
  const next = getStoredChatLocale() ?? detectChatLocale();
  if (next !== current) { current = next; emit(); }
}

export function setChatLocale(locale: ChatLocale): void {
  if (!SUPPORTED.has(locale)) return;
  persistChatLocale(locale);
  if (locale !== current) { current = locale; emit(); }
}

export function getActiveChatLocale(): ChatLocale { return current; }

export function chatLocaleMeta(locale: ChatLocale): ChatLocaleMeta {
  return LOCALE_BY_CODE[locale] ?? LOCALE_BY_CODE.en;
}

export function chatLocaleDir(locale: ChatLocale): 'ltr' | 'rtl' {
  return chatLocaleMeta(locale).dir;
}

/** The language name handed to the model in the system prompt. */
export function chatLanguageName(locale: ChatLocale): string {
  return chatLocaleMeta(locale).english;
}

// ── Translation with per-key English fallback ───────────────────────────────
export type ChatVars = Record<string, string | number>;

export function translate(locale: ChatLocale, key: string, vars?: ChatVars): string {
  const bundle = BUNDLES[locale];
  const raw = (bundle && bundle[key]) || BUNDLES.en[key] || key;
  if (!vars) return raw;
  return raw.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m));
}

export interface ChatI18n {
  locale: ChatLocale;
  dir: 'ltr' | 'rtl';
  meta: ChatLocaleMeta;
  t: (key: string, vars?: ChatVars) => string;
  setLocale: (locale: ChatLocale) => void;
  locales: ChatLocaleMeta[];
}

/** React hook — subscribes to the active locale and returns a bound translator. */
export function useChatI18n(): ChatI18n {
  const locale = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
  useEffect(() => { hydrateChatLocale(); }, []);
  return {
    locale,
    dir: chatLocaleDir(locale),
    meta: chatLocaleMeta(locale),
    t: (key: string, vars?: ChatVars) => translate(locale, key, vars),
    setLocale: setChatLocale,
    locales: CHAT_LOCALES,
  };
}

// ── Intl helpers ────────────────────────────────────────────────────────────
const FILE_UNITS = ['B', 'KB', 'MB', 'GB'];

/** Localized file size, e.g. 1,4 MB (pt-BR) — uses Intl for the number. */
export function formatFileSize(bytes: number, locale: ChatLocale): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return `0 ${FILE_UNITS[0]}`;
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < FILE_UNITS.length - 1) { value /= 1024; unit += 1; }
  const digits = unit === 0 ? 0 : value >= 10 ? 1 : 2;
  let num: string;
  try {
    num = new Intl.NumberFormat(locale, { maximumFractionDigits: digits, minimumFractionDigits: 0 }).format(value);
  } catch {
    num = value.toFixed(digits);
  }
  return `${num} ${FILE_UNITS[unit]}`;
}

export function formatNumber(value: number, locale: ChatLocale): string {
  try { return new Intl.NumberFormat(locale).format(value); } catch { return String(value); }
}

export function formatDate(value: string | number | Date, locale: ChatLocale, opts?: Intl.DateTimeFormatOptions): string {
  try {
    const d = value instanceof Date ? value : new Date(value);
    return new Intl.DateTimeFormat(locale, opts || { dateStyle: 'medium' }).format(d);
  } catch {
    return String(value);
  }
}

export function formatDateTime(value: string | number | Date, locale: ChatLocale): string {
  return formatDate(value, locale, { dateStyle: 'medium', timeStyle: 'short' });
}

/** Tailwind direction-aware helpers for the widget root. */
export function chatDirectionProps(locale: ChatLocale): { dir: 'ltr' | 'rtl'; lang: string } {
  return { dir: chatLocaleDir(locale), lang: locale };
}

// ── Lightweight in-message language detection (for the "switch UI" nudge) ───
const SCRIPT_TESTS: { re: RegExp; locale: ChatLocale }[] = [
  { re: /[\u3040-\u30ff\u4e00-\u9fff]/, locale: 'zh-CN' }, // Kana + Han
  { re: /[\uac00-\ud7af]/, locale: 'ko' }, // Hangul
  { re: /[\u0600-\u06ff]/, locale: 'ar' }, // Arabic
  { re: /[\u0900-\u097f]/, locale: 'hi' }, // Devanagari
];

const LATIN_HINTS: { locale: ChatLocale; words: string[] }[] = [
  { locale: 'es', words: ['hola', 'gracias', 'necesito', 'puedo', 'cómo', 'por favor', 'ayuda', 'tengo', 'estoy', 'dónde', 'cuánto', 'quisiera', 'servicios', 'trabajo'] },
  { locale: 'fr', words: ['bonjour', 'merci', 'besoin', 'pouvez', 'comment', 'aide', 'bonsoir', 'nous', 'avec', 'prix', 'combien', 'travail'] },
  { locale: 'pt-BR', words: ['olá', 'obrigado', 'obrigada', 'preciso', 'como', 'ajuda', 'você', 'gostaria', 'quanto', 'serviços', 'trabalho'] },
  { locale: 'tl', words: ['kumusta', 'salamat', 'kailangan', 'paano', 'tulong', 'meron', 'magkano', 'gusto', 'trabaho'] },
  { locale: 'vi', words: ['xin chào', 'cảm ơn', 'cần', 'như thế nào', 'giúp', 'tôi', 'chúng tôi', 'bao nhiêu', 'công việc'] },
];

/**
 * Best-effort detection of the language a visitor wrote in, from scripts and a
 * few high-signal words. Only used to OFFER a one-tap UI switch — never to
 * change anything on its own. Returns null when unsure.
 */
export function detectLocaleFromText(text: string): ChatLocale | null {
  const s = (text || '').trim();
  if (s.length < 3) return null;
  for (const t of SCRIPT_TESTS) if (t.re.test(s)) return t.locale;
  // Vietnamese is Latin-script but has distinctive diacritics.
  if (/[ăâđêôơư]|[ạảãèéẹẻẽìíịỉĩòóọỏõùúụủũỳýỵỷỹ]/i.test(s)) return 'vi';
  const lower = s.toLowerCase();
  let best: ChatLocale | null = null;
  let bestHits = 0;
  for (const { locale, words } of LATIN_HINTS) {
    let hits = 0;
    for (const w of words) { if (w.length >= 3 && lower.includes(w)) hits += 1; }
    if (hits > bestHits) { bestHits = hits; best = locale; }
  }
  return bestHits >= 1 ? best : null;
}